import { assertNever, mapObjectValues } from '@prisma/internals'
import Decimal from 'decimal.js'

import { JsonOutputTaggedValue } from '../engines'
import { JsOutputValue } from '../types/exported/JsApi'

export function deserializeJsonResponse(result: unknown): unknown {
  if (result === null) {
    return result
  }

  if (Array.isArray(result)) {
    return result.map(deserializeJsonResponse)
  }

  if (typeof result === 'object') {
    if (isTaggedValue(result)) {
      return deserializeTaggedValue(result)
    }

    // avoid mapping class instances
    if (result.constructor !== null && result.constructor.name !== 'Object') {
      return result
    }

    return mapObjectValues(result, deserializeJsonResponse)
  }

  return result
}

function isTaggedValue(value: unknown): value is JsonOutputTaggedValue {
  return value !== null && typeof value == 'object' && typeof value['$type'] === 'string'
}

function deserializeTaggedValue({ $type, value }: JsonOutputTaggedValue): JsOutputValue {
  switch ($type) {
    case 'BigInt':
      return BigInt(value)
    case 'Bytes': {
      const { buffer, byteOffset, byteLength } = Buffer.from(value, 'base64')
      return new Uint8Array(buffer, byteOffset, byteLength)
    }
    case 'DateTime':
      return new Date(value)
    case 'Decimal':
      return new Decimal(value)
    case 'Json':
      return JSON.parse(value)
    default:
      assertNever(value, 'Unknown tagged value')
  }
}
